import React from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useState, useEffect } from 'react';

import {
  createFood,
  getFoodById,
  updateFood,
  type UpdateFoodPayload
} from 'services/foodService';

import {
  FoodFormSections,
  type FoodFormData,
  type ServingUnit
} from './FoodFormSections';

const normalizeServingUnit = (
  value: string | null | undefined
): UpdateFoodPayload['serving_unit'] => {
  const normalized = value?.trim().toLowerCase();

  if (normalized === 'g' || normalized === 'ml' || normalized === 'item') {
    return normalized;
  }

  return null;
};

const normalizeNutritionKey = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const readAdditionalAmount = (
  additional: Record<string, unknown>,
  keys: string[]
): string => {
  if (!additional || typeof additional !== 'object') {
    return '';
  }

  for (const key of keys) {
    const rawValue = additional[key];

    if (rawValue === null || rawValue === undefined || rawValue === '') {
      continue;
    }

    if (typeof rawValue === 'object') {
      const nutrient = rawValue as {
        amount?: number | string | null;
        value?: number | string | null;
      };

      const amount = nutrient.amount ?? nutrient.value;

      if (amount !== null && amount !== undefined && amount !== '') {
        return String(amount);
      }

      continue;
    }

    return String(rawValue);
  }

  const normalizedKeyMap = new Map<string, unknown>();

  for (const [entryKey, entryValue] of Object.entries(additional)) {
    normalizedKeyMap.set(normalizeNutritionKey(entryKey), entryValue);
  }

  for (const key of keys) {
    const matched = normalizedKeyMap.get(normalizeNutritionKey(key));

    if (matched === null || matched === undefined || matched === '') {
      continue;
    }

    if (typeof matched === 'object') {
      const nutrient = matched as {
        amount?: number | string | null;
        value?: number | string | null;
      };

      const amount = nutrient.amount ?? nutrient.value;

      if (amount !== null && amount !== undefined && amount !== '') {
        return String(amount);
      }

      continue;
    }

    return String(matched);
  }

  return '';
};

const AddFood: React.FC = () => {
  const { id } = useParams<{
    id?: string;
  }>();

  const navigate = useNavigate();

  const isEditMode = Boolean(id);

  const foodId = id ? Number(id) : null;

  const [formData, setFormData] = useState<FoodFormData>({
    // Basic Information
    name_en: '',
    name_ar: '',
    brand_name_en: '',
    brand_name_ar: '',
    barcode: '',
    image_url: '',
    description: '',

    // Serving
    serving_size: '',
    serving_unit: '',

    // Core Nutrition
    calories: '',
    fat_g: '',
    protein_g: '',
    carbohydrates_g: '',
    fiber_g: '',
    sugar_g: '',
    sodium_mg: '',

    // Additional Nutrition
    trans_fat_g: '',
    saturated_fat_g: '',
    cholesterol_mg: '',
    added_sugars_g: '',
    vitamin_d_mcg: '',
    calcium_mg: '',
    iron_mg: '',
    potassium_mg: '',

    vitamin_a_rae_mcg: '',
    vitamin_c_mg: '',
    vitamin_b6_mg: '',
    vitamin_b12_mcg: '',
    magnesium_mg: '',
    zinc_mg: '',
    phosphorus_mg: '',
    folate_mcg: '',
    water_g: '',
    ash_g: '',
    vitamin_e_mg: '',
    vitamin_k_mcg: '',
    thiamin_mg: '',
    riboflavin_mg: '',
    niacin_mg: '',
    pantothenic_acid_mg: '',
    copper_mg: '',
    manganese_mg: '',
    selenium_mcg: '',
    monounsaturated_fat_g: '',
    polyunsaturated_fat_g: '',
    caffeine_mg: ''
  });

  const [loadingFood, setLoadingFood] = useState(false);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEditMode || !foodId) {
      return;
    }

    const loadFood = async () => {
      try {
        setLoadingFood(true);

        const food = await getFoodById(foodId);

        /*
         * Convert API response
         * into form values.
         */

        const additional =
          typeof food.nutrition?.additional === 'string'
            ? JSON.parse(food.nutrition.additional)
            : food.nutrition?.additional || {};

        const defaultMeasure =
          food.measures?.find(measure => measure.is_default) ||
          food.measures?.[0];

        setFormData(prev => ({
          ...prev,

          // =========================
          // Basic information
          // =========================

          name_en: food.name_en || '',

          name_ar: food.name_ar || '',

          description: food.description || '',

          brand_name_en: food.brand?.name_en || '',

          brand_name_ar: food.brand?.name_ar || '',

          barcode: food.barcode || '',

          image_url: food.image_url || '',

          // =========================
          // Serving
          // =========================

          serving_size: defaultMeasure?.quantity?.toString() || '',

          serving_unit: normalizeServingUnit(defaultMeasure?.unit) || '',

          // =========================
          // Core nutrition
          // =========================

          calories: food.nutrition?.calories?.toString() || '',

          protein_g: food.nutrition?.protein_g?.toString() || '',

          fat_g: food.nutrition?.fat_g?.toString() || '',

          carbohydrates_g: food.nutrition?.carbohydrates_g?.toString() || '',

          fiber_g: food.nutrition?.fiber_g?.toString() || '',

          sugar_g: food.nutrition?.sugar_g?.toString() || '',

          sodium_mg: food.nutrition?.sodium_mg?.toString() || '',

          // =========================
          // Additional nutrition
          // =========================

          trans_fat_g: readAdditionalAmount(additional, [
            'trans_fat_g',
            'Fatty acids, total trans',
            'Fatty acids, total trans-monoenoic'
          ]),

          saturated_fat_g: readAdditionalAmount(additional, [
            'saturated_fat_g',
            'Fatty acids, total saturated'
          ]),

          cholesterol_mg: readAdditionalAmount(additional, [
            'cholesterol_mg',
            'Cholesterol'
          ]),

          added_sugars_g: readAdditionalAmount(additional, [
            'added_sugars_g',
            'Sugars, added'
          ]),

          vitamin_d_mcg: readAdditionalAmount(additional, [
            'vitamin_d_mcg',
            'Vitamin D (D2 + D3)',
            'Vitamin D'
          ]),

          calcium_mg: readAdditionalAmount(additional, [
            'calcium_mg',
            'Calcium, Ca'
          ]),

          iron_mg: readAdditionalAmount(additional, ['iron_mg', 'Iron, Fe']),

          potassium_mg: readAdditionalAmount(additional, [
            'potassium_mg',
            'Potassium, K'
          ]),

          vitamin_a_rae_mcg: readAdditionalAmount(additional, [
            'vitamin_a_rae_mcg',
            'Vitamin A, RAE'
          ]),

          vitamin_c_mg: readAdditionalAmount(additional, [
            'vitamin_c_mg',
            'Vitamin C, total ascorbic acid'
          ]),

          vitamin_b6_mg: readAdditionalAmount(additional, [
            'vitamin_b6_mg',
            'Vitamin B-6'
          ]),

          vitamin_b12_mcg: readAdditionalAmount(additional, [
            'vitamin_b12_mcg',
            'Vitamin B-12'
          ]),

          magnesium_mg: readAdditionalAmount(additional, [
            'magnesium_mg',
            'Magnesium, Mg'
          ]),

          zinc_mg: readAdditionalAmount(additional, ['zinc_mg', 'Zinc, Zn']),

          phosphorus_mg: readAdditionalAmount(additional, [
            'phosphorus_mg',
            'Phosphorus, P'
          ]),

          folate_mcg: readAdditionalAmount(additional, [
            'folate_mcg',
            'Folate, total'
          ]),

          water_g: readAdditionalAmount(additional, ['water_g', 'Water']),

          ash_g: readAdditionalAmount(additional, ['ash_g', 'Ash']),

          vitamin_e_mg: readAdditionalAmount(additional, [
            'vitamin_e_mg',
            'Vitamin E (alpha-tocopherol)'
          ]),

          vitamin_k_mcg: readAdditionalAmount(additional, [
            'vitamin_k_mcg',
            'Vitamin K (phylloquinone)'
          ]),

          thiamin_mg: readAdditionalAmount(additional, [
            'thiamin_mg',
            'Thiamin'
          ]),

          riboflavin_mg: readAdditionalAmount(additional, [
            'riboflavin_mg',
            'Riboflavin'
          ]),

          niacin_mg: readAdditionalAmount(additional, ['niacin_mg', 'Niacin']),

          pantothenic_acid_mg: readAdditionalAmount(additional, [
            'pantothenic_acid_mg',
            'Pantothenic acid'
          ]),

          copper_mg: readAdditionalAmount(additional, [
            'copper_mg',
            'Copper, Cu'
          ]),

          manganese_mg: readAdditionalAmount(additional, [
            'manganese_mg',
            'Manganese, Mn'
          ]),

          selenium_mcg: readAdditionalAmount(additional, [
            'selenium_mcg',
            'Selenium, Se'
          ]),

          monounsaturated_fat_g: readAdditionalAmount(additional, [
            'monounsaturated_fat_g',
            'Fatty acids, total monounsaturated'
          ]),

          polyunsaturated_fat_g: readAdditionalAmount(additional, [
            'polyunsaturated_fat_g',
            'Fatty acids, total polyunsaturated'
          ]),

          caffeine_mg: readAdditionalAmount(additional, [
            'caffeine_mg',
            'Caffeine'
          ])
        }));
      } catch (error: any) {
        toast.error(error?.message || 'Failed to load food.');

        navigate('/superadmin/food');
      } finally {
        setLoadingFood(false);
      }
    };

    loadFood();
  }, [isEditMode, foodId, navigate]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof FoodFormData;
    const nextValue =
      fieldName === 'serving_unit'
        ? value === ''
          ? ''
          : (value as ServingUnit)
        : value;

    setFormData(
      prev =>
        ({
          ...prev,
          [fieldName]: nextValue
        }) as FoodFormData
    );
  };

  const toNullableNumber = (value: string): number | null => {
    if (value.trim() === '') {
      return null;
    }

    return Number(value);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);

      if (!formData.name_en.trim()) {
        toast.error('English food name is required.');
        return;
      }

      if (!formData.name_ar.trim()) {
        toast.error('Arabic food name is required.');
        return;
      }

      const normalizedServingUnit: UpdateFoodPayload['serving_unit'] =
        formData.serving_unit === '' ? null : formData.serving_unit;

      const nutrition_json = {
        trans_fat_g: toNullableNumber(formData.trans_fat_g),

        saturated_fat_g: toNullableNumber(formData.saturated_fat_g),

        cholesterol_mg: toNullableNumber(formData.cholesterol_mg),

        added_sugars_g: toNullableNumber(formData.added_sugars_g),

        vitamin_d_mcg: toNullableNumber(formData.vitamin_d_mcg),

        calcium_mg: toNullableNumber(formData.calcium_mg),

        iron_mg: toNullableNumber(formData.iron_mg),

        potassium_mg: toNullableNumber(formData.potassium_mg),

        vitamin_a_rae_mcg: toNullableNumber(formData.vitamin_a_rae_mcg),

        vitamin_c_mg: toNullableNumber(formData.vitamin_c_mg),

        vitamin_b6_mg: toNullableNumber(formData.vitamin_b6_mg),

        vitamin_b12_mcg: toNullableNumber(formData.vitamin_b12_mcg),

        magnesium_mg: toNullableNumber(formData.magnesium_mg),

        zinc_mg: toNullableNumber(formData.zinc_mg),

        phosphorus_mg: toNullableNumber(formData.phosphorus_mg),

        folate_mcg: toNullableNumber(formData.folate_mcg),

        water_g: toNullableNumber(formData.water_g),

        ash_g: toNullableNumber(formData.ash_g),

        vitamin_e_mg: toNullableNumber(formData.vitamin_e_mg),

        vitamin_k_mcg: toNullableNumber(formData.vitamin_k_mcg),

        thiamin_mg: toNullableNumber(formData.thiamin_mg),

        riboflavin_mg: toNullableNumber(formData.riboflavin_mg),

        niacin_mg: toNullableNumber(formData.niacin_mg),

        pantothenic_acid_mg: toNullableNumber(formData.pantothenic_acid_mg),

        copper_mg: toNullableNumber(formData.copper_mg),

        manganese_mg: toNullableNumber(formData.manganese_mg),

        selenium_mcg: toNullableNumber(formData.selenium_mcg),

        monounsaturated_fat_g: toNullableNumber(formData.monounsaturated_fat_g),

        polyunsaturated_fat_g: toNullableNumber(formData.polyunsaturated_fat_g),

        caffeine_mg: toNullableNumber(formData.caffeine_mg)
      };

      const payload = {
        name_en: formData.name_en.trim(),

        name_ar: formData.name_ar.trim() || null,

        description: formData.description.trim() || null,

        brand_name_en: formData.brand_name_en.trim() || null,

        brand_name_ar: formData.brand_name_ar.trim() || null,

        serving_size: toNullableNumber(formData.serving_size),

        serving_unit: normalizedServingUnit,

        calories: toNullableNumber(formData.calories),

        protein_g: toNullableNumber(formData.protein_g),

        fat_g: toNullableNumber(formData.fat_g),

        carbohydrates_g: toNullableNumber(formData.carbohydrates_g),

        fiber_g: toNullableNumber(formData.fiber_g),

        sugar_g: toNullableNumber(formData.sugar_g),

        sodium_mg: toNullableNumber(formData.sodium_mg),

        nutrition_json,

        barcode: formData.barcode.trim() || null,

        image_url: formData.image_url.trim() || null
      };

      console.log(
        isEditMode ? 'Update food payload:' : 'Create food payload:',
        payload
      );

      if (isEditMode && foodId) {
        const result = await updateFood(foodId, payload);

        console.log('Food updated:', result);

        toast.success('Food updated successfully.');

        navigate('/superadmin/food');

        return;
      }

      /*
       * ========================================
       * ADD MODE
       * ========================================
       */

      const result = await createFood(payload);

      console.log('Food created:', result);

      toast.success('Food created successfully.');

      navigate('/superadmin/food');
    } catch (error: any) {
      console.error(
        isEditMode ? 'Failed to update food:' : 'Failed to create food:',
        error
      );

      toast.error(
        error?.message ||
          (isEditMode ? 'Failed to update food.' : 'Failed to create food.')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <FoodFormSections
      formData={formData}
      onChange={handleChange}
      isEditMode={isEditMode}
      loadingFood={loadingFood}
      saving={saving}
      onSubmit={handleSubmit}
      onCancel={() => navigate('/superadmin/food')}
    />
  );
};

export default AddFood;
